import ValueSelector, { IValueSet } from '../../../../../common/scripts/Toolkit/Helper/ValueSelector';
import Model from '../../../../../common/scripts/Toolkit/Helper/Model';
import TableModel from './TableModel';
import DataType from '../Config/DataStructure';
import TableGame from '../../../System/TableGame';
import UserModel from './UserModel';
import { PathName } from '../Config/PathConfig';
import TableErrorName from '../Config/TableErrorName';

enum EventType {
    USER_BET_CHANGED = 'USER_BET_CHANGED',
    IS_CONFIRMING_STATE_CHANGED = 'IS_CONFIRMING_STATE_CHANGED',
    HOLDING_BET_STATE_CHANGED = 'HOLDING_BET_STATE_CHANGED',
}

enum ValueSelectorType {
    Denom,
}

// TODO: MSlot (Don't need BettingModel, just overwrite it in LobbyBettingModel)
export default class BettingModel extends Model {
    public static readonly EVENT_TYPE = EventType;

    protected readonly _userModel: UserModel = TableGame.user;
    protected _valueSelectorOffset: { [value in ValueSelectorType]: number };
    protected _denomValueSelector: ValueSelector<number> = null;
    protected readonly _defaultDenomValueIndex: number = 0;

    protected _holdingBet = false;
    protected _userBet = 0;
    protected _isComfirming = false;
    protected _isBetCancelled = false;
    protected _isReconnected = false;

    constructor(protected readonly _tableModel: TableModel) {
        super();
        this._valueSelectorOffset = {
            [ValueSelectorType.Denom]: 0,
        };
        this.init();
    }

    public get denomValueSelector() {
        return this._denomValueSelector;
    }

    public get isComfirming() {
        return this._isComfirming;
    }
    protected set isComfirming(flag: boolean) {
        if (this._isComfirming === flag) {
            return;
        }
        this._isComfirming = flag;
        this.emit(BettingModel.EVENT_TYPE.IS_CONFIRMING_STATE_CHANGED);
    }

    public get totalBet() {
        return this.denomValueSelector.value;
    }

    public get userBet() {
        return this._userBet;
    }
    protected set userBet(value: number) {
        if (value === this._userBet) {
            return;
        }
        this._userBet = value;
        this.emit(BettingModel.EVENT_TYPE.USER_BET_CHANGED);
    }

    public get holdingBet() {
        return this._holdingBet;
    }
    protected set holdingBet(flag: boolean) {
        if (this._holdingBet === flag) {
            return;
        }
        this._holdingBet = flag;
        this.emit(BettingModel.EVENT_TYPE.HOLDING_BET_STATE_CHANGED);
    }

    public get isBetCancelled() {
        return this._isBetCancelled;
    }

    // model operations
    public init(): void {
        TableGame.connection.on(PathName.Bet, this.onReceiveBetData, this);
        TableGame.connection.on(`${PathName.Bet}_error`, this.onReceiveBetError, this);
        this._tableModel.on(TableModel.EVENT_TYPE.EXIT_TABLE, this.onExitTable, this);
        this._tableModel.on(TableModel.EVENT_TYPE.STATE_CHANGED, this.onTableStateChanged, this);
    }

    public initConfig(config: DataType.Table.ConfigData): void {
        this._denomValueSelector = new ValueSelector(this.toValueSet(config.DenomConfig, ValueSelectorType.Denom));

        this._denomValueSelector.on(ValueSelector.EVENT_TYPE.VALUE_CHANGED, () => {
            // TableGame.account.denom = this._denomValueSelector.value;
            TableGame.hall.currentGameData.updateGameDenom();
        });
    }

    public async bet(): Promise<[boolean, string]> {
        const { totalBet } = this;
        const [isSuccess, reason] = this.checkBet(totalBet);
        if (!isSuccess) {
            return [isSuccess, reason];
        }
        this.userBet = totalBet;
        return this.sendToServer();
    }

    public reconnect(isSuccess: boolean = true) {
        this._isReconnected = isSuccess;
    }

    public checkBalanceLessThanBet(newBet: number, needCreditToValue: boolean = true): boolean {
        const betValue = needCreditToValue ? TableGame.account.creditToValue(newBet) : newBet;
        return betValue > this._userModel.balance;
    }

    /**
     * Check if the new bet is valid
     * @param totalBet new added bet value
     * @returns [isValid, errorName]
     */
    protected checkBet(totalBet: number): [boolean, string] {
        // user.balance has subtracted the current bet, so we can compare it directly
        if (totalBet > TableGame.user.gameCredit) {
            return [false, TableErrorName.INSUFFICIENT_BALANCE];
        }
        return [true, null];
    }

    protected async sendToServer(): Promise<any> {
        return Promise.resolve();
    }

    // event callbacks
    protected onExitTable() {
        this.denomValueSelector.currentIdx = this._defaultDenomValueIndex;
    }

    protected onTableStateChanged() {
        if (this._tableModel.state === DataType.Enums.TableState.Waiting || this._tableModel.state === DataType.Enums.TableState.Cancel) {
            if (this._tableModel.state === DataType.Enums.TableState.Cancel && this.userBet) {
                this._isBetCancelled = true;
            } else {
                this._isBetCancelled = false;
            }
            this.userBet = 0;
            this._isReconnected = false;
        } else if (this._tableModel.state === DataType.Enums.TableState.Betting) {
            this.holdingBet = true;
        }
    }

    protected onReceiveBetData() {
        this.holdingBet = false;
    }

    protected onReceiveBetError(err: string) {
        this.holdingBet = false;
        if (err !== TableErrorName.INSUFFICIENT_BALANCE) {
            TableGame.error.handle(err);
        }
    }

    protected toValueSet(valueList: DataType.Table.ValueList, valueType: ValueSelectorType): IValueSet<number> {
        const originValueLength = valueList.Values.length;
        valueList.Values = valueList.Values.filter((value) => value !== 0);
        this._valueSelectorOffset[valueType] = originValueLength - valueList.Values.length;
        return {
            defaultIndex: valueList.Default - this._valueSelectorOffset[valueType],
            values: valueList.Values,
        };
    }
}
